import SchoolReadingHistory from './SchoolReadingHistory';
import React, { useEffect, useState } from 'react';

import WorkbenchModule from './WorkbenchModule';

import { Alert, Button, Collapse, Empty, Progress, Select, Space, Spin, Table, Tabs, Tag, Tooltip, message } from 'antd';

import { ArrowRightOutlined, DownOutlined, BellOutlined, CheckSquareOutlined, DesktopOutlined, PushpinOutlined, PushpinFilled, ReloadOutlined, SendOutlined, TeamOutlined, UserSwitchOutlined } from '@ant-design/icons';

import { useAuth } from '../../contexts/AuthContext';

import { resolveWorkbenchOperation, workbenchPreferenceKey } from './schoolWorkbenchPreference';

import SchoolJoinSettings from './SchoolJoinSettings';

import './SchoolWorkbenchView.css';



const number = (value, available = true) => available ? Number(value || 0) : '—';

const Metric = ({ label, value, unit }) => <div className="sw-metric"><span>{label}</span><strong>{value}<small>{unit}</small></strong></div>;

const Section = ({ title, extra, children, className = '' }) => <section className={`sw-section ${className}`}>

  <div className="sw-section-heading"><h2>{title}</h2>{extra}</div>{children}

</section>;



export default function SchoolWorkbenchView({ schools, schoolId, schoolOptions, selectSchool, platformManager, showNotifications,

  loading, overview, workbench, grades, teachers, devices, failedModules, refresh, navigate,

  profileMissing, schoolRegion, onboardingChecklist, readyPercent, gradeColumns, teacherColumns, deviceColumns,

  openTeacherImport, openQrcodeGenerator, formatBytes }) {

  const [tab, setTab] = useState('business');

  const [devicesOpen, setDevicesOpen] = useState(false);

  const [operationState, setOperation] = useState(null);

  const [pinnedState, setPinnedPath] = useState(null);

  const { user } = useAuth();

  const preferenceKey = workbenchPreferenceKey(user?.id || user?.username, schoolId);

  const operationScope = preferenceKey || `school:${schoolId}`;

  const operation = operationState?.scope === operationScope ? operationState : null;

  const pinnedPath = pinnedState?.scope === operationScope ? pinnedState.path : null;

  useEffect(() => {

    let restored = null;

    try {

      restored = preferenceKey ? resolveWorkbenchOperation(localStorage.getItem(preferenceKey), schoolId, showNotifications, platformManager) : null;

    } catch { /* Storage can be unavailable; ordinary operation still works. */ }

    setOperation(restored ? { ...restored, scope: operationScope } : null);

    setPinnedPath({ path: restored?.path || null, scope: operationScope });

    setDevicesOpen(restored?.path === 'devices');

    setTab('business');

  }, [preferenceKey, operationScope, schoolId, showNotifications, platformManager]);

  const openOperation = (path, title = '管理操作') => {

    setTab('business');

    setOperation({ path, title, scope: operationScope });

  };

  const openGrade = grade => openOperation(`/grade-management?schoolId=${schoolId}${grade ? `&grade=${encodeURIComponent(grade)}` : ''}&tab=managers`, '年级负责人');

  const school = overview?.school || {};

  const totals = overview?.totals || {}, today = overview?.today || {};

  const schoolToday = workbench?.today || {}, coverage = workbench?.coverage || {}, warnings = workbench?.warnings || {};

  const schoolName = school.name || schools.find(row => String(row.id) === schoolId)?.name;

  const disabled = !schoolId || loading;

  const gradeLink = () => openGrade();

  const gradePath = `/grade-management?schoolId=${schoolId}&tab=managers`;

  const quickActions = [

    ...(showNotifications ? [

      { title: '发布通知', desc: '全校或指定教师', icon: <BellOutlined />, path: `/school-operations?schoolId=${schoolId}&action=notice` },

      { title: '安排任务', desc: '分派与跟进任务', icon: <CheckSquareOutlined />, path: `/school-operations?schoolId=${schoolId}&action=task` }

    ] : []),

    { title: '发送广播 / 文件', desc: '向班级或年级发送', icon: <SendOutlined />, path: `/broadcast?schoolId=${schoolId}&action=send` },

    { title: '教师管理', desc: '邀请码与班级权限', icon: <TeamOutlined />, path: 'staff' },

    { title: '年级负责人', desc: '权限与授权期限', icon: <UserSwitchOutlined />, path: gradePath },

    { title: '设备管理', desc: '在线设备与年级控制', icon: <DesktopOutlined />, path: 'devices' }

  ];

  const toggleQuickAction = item => {

    if (operation?.path === item.path) { setOperation(null); return; }

    if (item.path === 'devices') setDevicesOpen(true);

    openOperation(item.path, item.title);

  };

  const togglePinned = () => {

    if (!preferenceKey || !operation) return;

    const isPinned = pinnedPath === operation.path;

    try {

      if (isPinned) localStorage.removeItem(preferenceKey);

      else localStorage.setItem(preferenceKey, operation.path);

      setPinnedPath({ path: isPinned ? null : operation.path, scope: operationScope });

      message.success(isPinned ? '已取消固定' : '已固定，下次进入此学校工作台将自动展开');

    } catch { message.warning('浏览器无法保存固定设置，请检查浏览器存储设置'); }

  };

  const entryGroups = [

    { title: '班级与教学', items: [

      ['班级管理', '班级资料、成员与班级配置', '/classes'],

      ['学生资料', '学生档案、分班与资料导入', '/students'],

      ['课程表', '维护各班课程安排', '/classes/schedule'],

      ['值日表', '维护各班值日安排', '/classes/duty'],

      ['座位表', '查看、编排与导入座位', '/classes/seat'],

      ['成绩管理', '成绩导入、查询与发布', '/students/grades']

    ] },

    { title: '通知与协作', items: [

      ['广播管理', '发送文字、图片、文档和视频', '/broadcast'],

      ...(showNotifications ? [['学校通知与任务', '全校通知、任务分发与反馈', '/school-operations']] : []),

      ['班级待办', '查看各班待办与反馈', '/classes/todos'],

      ['家长绑定', '核查学生与家长的绑定关系', '/classes/parent-binding'],

      ['考勤管理', '手动考勤与出勤记录', '/smart-attendance?tab=manual'],

      ['视频看班', '查看班级视频', '/smart-attendance?tab=video'],

      ['积分管理', '学生积分与奖励操作', '/students/points-new']

    ] }

  ];



  const dashboard = <div className="sw-pane">

    <div className="sw-summary" aria-label="学校核心统计">

      <Metric label="班级总数" value={number(totals.classes, Boolean(overview))} unit="个" />

      <Metric label="教师人数" value={number(totals.teachers, Boolean(overview))} unit="人" />

      <Metric label="学生人数" value={number(totals.students, Boolean(overview))} unit="人" />

      <Metric label="5分钟在线班级" value={number(totals.onlineClasses5m, Boolean(overview))} unit="个" />

    </div>

    <div className="sw-dashboard-grid">

      <Section title="今日学生情况" extra={<span className="sw-muted">当前学校</span>}>

        <div className="sw-metric-grid">

          <Metric label="缺勤学生" value={number(schoolToday.absentStudents, Boolean(workbench))} unit="人" />

          <Metric label="请假学生" value={number(schoolToday.leaveStudents, Boolean(workbench))} unit="人" />

          <Metric label="迟到学生" value={number(schoolToday.lateStudents, Boolean(workbench))} unit="人" />

          <Metric label="违纪学生" value={number(schoolToday.violationStudents, Boolean(workbench))} unit="人" />

          <Metric label="学生谈话" value={number(schoolToday.studentConversations, Boolean(workbench))} unit="条" />

          <Metric label="家长绑定率" value={number(coverage.parentBindingRate, Boolean(workbench))} unit="%" />

        </div>

      </Section>

      <Section title="班级配置覆盖" extra={<Button type="link" disabled={disabled} onClick={() => setTab('business')}>进入管理 <ArrowRightOutlined /></Button>}>

        <dl className="sw-coverage">

          {[

            ['全校班级', coverage.totalClasses], ['已绑定设备', coverage.classesWithDevice],

            ['已配置课程表', coverage.classesWithSchedule], ['已配置值日表', coverage.classesWithDuty],

            ['未绑定家长学生', coverage.unboundStudents]

          ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{number(value, Boolean(workbench))}</dd></div>)}

        </dl>

      </Section>

    </div>

    {workbench && <Alert showIcon type={warnings.classesMissingDevices?.length || warnings.classesMissingSchedules?.length ? 'warning' : 'success'}

      message={`未绑定设备班级 ${warnings.classesMissingDevices?.length || 0} 个 · 未配置课程表班级 ${warnings.classesMissingSchedules?.length || 0} 个`} />}

    <Section title="基础数据准备情况" extra={<span className="sw-muted">{onboardingChecklist.filter(item => item.done).length} / {onboardingChecklist.length} 项已有数据</span>}>

      <Progress percent={readyPercent} strokeColor="#1677ff" />

      <Collapse ghost items={[{ key: 'readiness', label: '查看准备明细', children:

        <div className="sw-checklist">{onboardingChecklist.map(item => <div key={item.key}>

          <span className={`sw-status-dot ${item.done ? 'is-ready' : ''}`} />

          <div><strong>{item.title}</strong><p>{item.desc}</p></div><span className="sw-muted">{item.done ? '已有数据' : '待补充'}</span>

        </div>)}</div>

      }]} />

    </Section>

    <Section title="今日业务活动">

      <div className="sw-activity">

        {[['广播', today.broadcasts], ['班级待办', today.classTodos], ['待办反馈', today.classTodoFeedback], ['学生联系老师', today.studentMessages]]

          .map(([label, value]) => <Metric key={label} label={label} value={number(value, Boolean(overview))} />)}

      </div>

      <Collapse ghost items={[{ key: 'details', label: '查看导入与操作统计', children: <div className="sw-detail-metrics">

        {[['邀请码', today.invitations], ['上传文件', today.files], ['上传容量', overview ? formatBytes(today.uploadedBytes) : '—'],

          ['学生资料行', today.studentRows], ['积分操作', today.pointOperations], ['课程表行', today.courseRows], ['成绩行', today.gradeRows]]

          .map(([label, value]) => <div key={label}><span>{label}</span><strong>{typeof value === 'string' ? value : number(value, Boolean(overview))}</strong></div>)}

      </div> }]} />

    </Section>

  </div>;



  const profile = <div className="sw-pane">

    <Section title="学校基本资料" extra={platformManager && <Button disabled={disabled} onClick={() => openOperation(`/schools/${schoolId}/edit`, '编辑学校资料')}>编辑学校资料</Button>}>

      <dl className="sw-profile">

        {[

          ['学校名称', schoolName || '—'], ['所在地区', schoolRegion || '未完善'],

          ['联系电话', school.phone || '未完善'], ['详细地址', school.address || '未完善']

        ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}

        <div><dt>学校状态</dt><dd><Tag color={school.status === 'active' ? 'green' : 'default'}>{school.status === 'active' ? '已启用' : school.status || '待确认'}</Tag>

          {profileMissing.length > 0 && <span className="sw-muted">资料待完善</span>}</dd></div>

      </dl>

    </Section>

    <Section title="学校加入设置">

      <SchoolJoinSettings key={schoolId} schoolId={schoolId} disabled={disabled} />

      <div className="sw-inline-actions"><div><strong>邀请码与加入二维码</strong><p>教师邀请码、班级教师及家长加入代码在人员与权限中管理。</p></div>

        <Button disabled={disabled} onClick={() => setTab('staff')}>管理加入方式</Button></div>

    </Section>

  </div>;



  const staff = <div className="sw-pane">

    <Section title="教师与权限" extra={<Space wrap>

      <Button type="primary" disabled={disabled} onClick={openTeacherImport}>生成教师邀请码</Button>

      <Button disabled={disabled} onClick={gradeLink}>设置年级负责人</Button>

    </Space>}>

      <p className="sw-section-note">本校教师与班级权限；年级负责人可单独设置授权范围和有效期。</p>

      <Table rowKey={row => `${row.id}:${row.role}`} size="middle" loading={loading} columns={teacherColumns} dataSource={teachers} pagination={{ pageSize: 10 }} scroll={{ x: 850 }} />

    </Section>

    <Section title="加入与绑定">

      <div className="sw-inline-actions"><div><strong>全校班级二维码</strong><p>按班级生成家长或教师加入代码，默认复用有效代码。</p></div>

        <Button disabled={disabled} onClick={openQrcodeGenerator}>生成加入代码</Button></div>

      <div className="sw-inline-actions"><div><strong>家长绑定核查</strong><p>查看学生和家长的绑定情况。</p></div>

        <Button disabled={disabled} onClick={() => openOperation('/classes/parent-binding', '家长绑定')}>查看绑定</Button></div>

    </Section>

  </div>;





  const devicePanel = <div>

    <Section title="年级与设备" extra={<Button disabled={disabled} onClick={gradeLink}>发送文件 / 管理年级</Button>}>

      <Table rowKey="grade" size="middle" loading={loading} columns={gradeColumns.map(column => column.key === 'action' ? { ...column, render: (_, row) => <Space><Button size="small" onClick={() => openGrade(row.grade)}>负责人／文件</Button>{column.render(_, row)}</Space> } : column)} dataSource={grades} pagination={false} scroll={{ x: 720 }} />

    </Section>

    <Collapse className="sw-devices" activeKey={devicesOpen ? ['devices'] : []} onChange={keys => setDevicesOpen(keys.includes('devices'))} items={[{ key: 'devices', label: `桌面客户端设备（${devices.length} 台）`, children:

      <Table rowKey="id" size="middle" loading={loading} columns={deviceColumns} dataSource={devices} pagination={{ pageSize: 10 }} scroll={{ x: 800 }} /> }]} />

  </div>;



  const business = <div className="sw-pane">

    <Section title="常用操作" className="sw-quick-section">

      <div className="sw-shortcuts" data-count={quickActions.length} style={{ '--sw-action-count': quickActions.length }}>{quickActions.map(item => <button key={item.path} type="button" disabled={disabled} aria-expanded={operation?.path === item.path} className={operation?.path === item.path ? 'is-active' : ''} onClick={() => toggleQuickAction(item)}>

        <span className="sw-shortcut-icon">{item.icon}</span><span><strong>{item.title}</strong><span>{item.desc}</span></span><DownOutlined />

      </button>)}</div>

    </Section>

    {operation && <section className="sw-operation" aria-label="当前操作区">

      <div className="sw-section-heading"><h2>{operation.title}</h2><Space className="sw-operation-actions" wrap>

        <Tooltip title="固定后，下次进入此学校工作台自动展开。只记住入口，不保存表单内容。">

          <Button icon={pinnedPath === operation.path ? <PushpinFilled /> : <PushpinOutlined />} aria-pressed={pinnedPath === operation.path}

            aria-label={pinnedPath === operation.path ? '取消固定' : '固定'}

            type={pinnedPath === operation.path ? 'primary' : 'default'} ghost={pinnedPath === operation.path}

            disabled={!preferenceKey || !resolveWorkbenchOperation(operation.path, schoolId, showNotifications, platformManager)} onClick={togglePinned}>

            {pinnedPath === operation.path ? '取消固定' : '固定'}

          </Button>

        </Tooltip><Button onClick={() => setOperation(null)}>收起操作</Button>

      </Space></div>

      {operation.path === 'staff' ? staff : operation.path === 'devices' ? devicePanel : <WorkbenchModule key={`${schoolId}:${operation.path}`} schoolId={schoolId} schoolName={schoolName} path={operation.path} />}

    </section>}

    <div className="sw-entry-groups">{entryGroups.map(group => <Section key={group.title} title={group.title}>

      <div className="sw-entry-list">{group.items.map(([title, desc, path]) => <button key={title} type="button" disabled={disabled} onClick={() => openOperation(path, title)}>

        <span><strong>{title}</strong><span>{desc}</span></span><ArrowRightOutlined />

      </button>)}</div>

    </Section>)}</div>

    {operation?.path !== 'devices' && devicePanel}

  </div>;





  return <div className="school-workbench">

    <header className="sw-header"><div><h1>学校工作台</h1><p>{schoolName || '选择学校后开始管理'}</p></div>

      <Space wrap>{(platformManager || schools.length > 1) && <Select aria-label="选择管理学校" style={{ width: 250 }}

        placeholder="选择学校" value={schoolId} options={schoolOptions} onChange={selectSchool} />}

        <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading} disabled={!schoolId}>刷新</Button></Space>

    </header>

    {failedModules.length > 0 && <Alert className="sw-load-error" showIcon type="warning" message="部分学校数据暂未加载" description={`未加载：${failedModules.join('、')}。请刷新重试。`} />}

    {!schoolId ? <Empty description="暂无可管理的学校" /> : <Spin spinning={loading}>

      <Tabs className="sw-capsules" activeKey={tab} onChange={setTab} destroyOnHidden animated={false} items={[
        { key: 'reading', label: '早读数据', children: <SchoolReadingHistory schoolId={schoolId} /> },

        { key: 'business', label: '工作台', children: business }, { key: 'dashboard', label: '仪表盘', children: dashboard },

        { key: 'staff', label: '人员与权限', children: staff }, { key: 'profile', label: '学校设置', children: profile }

      ]} />

    </Spin>}

  </div>;

}

